/**
 * Credential Store for Registry Sync
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { credentialApi } from '../api/credentialApi';
import type { Credential, CredentialFormData, CredentialListParams } from '../types/credential';

export const useCredentialStore = defineStore('credential', () => {
    // State
    const credentials = ref<Credential[]>([]);
    const selectedCredential = ref<Credential | null>(null);
    const isLoading = ref(false);

    // Getters
    const hasCredentials = computed(() => credentials.value.length > 0);
    const credentialCount = computed(() => credentials.value.length);

    // Actions
    const loadCredentials = async (params?: CredentialListParams) => {
        isLoading.value = true;
        try {
            const response = await credentialApi.getCredentials(params);
            // apiClient는 response.data를 반환하므로, response 자체가 데이터
            const credentialList = Array.isArray(response) ? response : (response as any)?.data || [];
            credentials.value = Array.isArray(credentialList) ? credentialList : [];
        } catch (error: any) {
            console.error('Failed to load credentials:', error);
            const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to load credentials.';
            throw new Error(errorMessage);
        } finally {
            isLoading.value = false;
        }
    };

    const addCredential = async (data: CredentialFormData): Promise<Credential> => {
        isLoading.value = true;
        try {
            const response = await credentialApi.createCredential(data);
            // apiClient는 response.data를 반환하므로, response 자체가 데이터
            const newCredential = (response as any)?.data || response;
            
            if (!newCredential) {
                throw new Error('Invalid credential creation response.');
            }

            // 목록에 추가
            credentials.value.push(newCredential);
            return newCredential;
        } catch (error: any) {
            console.error('Failed to add credential:', error);

            // REGC001: REGISTRY_TYPE + USER_ID 중복 (이미 등록된 credential)
            const errorCode = error?.response?.data?.error?.code;
            if (errorCode === 'REGC001') {
                const e: any = new Error('REGC001');
                e.code = 'REGC001';
                e.registryType = data.registryType;
                throw e;
            }

            const fallbackMessage = error.response?.data?.error?.message || error.message || 'Failed to create credential.';
            throw new Error(fallbackMessage);
        } finally {
            isLoading.value = false;
        }
    };

    const updateCredential = async (id: string, data: Partial<CredentialFormData>): Promise<void> => {
        isLoading.value = true;
        try {
            await credentialApi.updateCredential(id, data);

            // 로컬 상태 업데이트
            const index = credentials.value.findIndex(c => c.id === id);
            if (index !== -1) {
                credentials.value[index] = {
                    ...credentials.value[index],
                    ...data,
                    password: '********', // 비밀번호는 항상 마스킹
                    updatedAt: new Date().toISOString()
                };

                // 선택된 자격 증명도 업데이트
                if (selectedCredential.value?.id === id) {
                    selectedCredential.value = credentials.value[index];
                }
            }

            // 목록 새로고침 (서버에서 최신 데이터 가져오기)
            await loadCredentials();
        } catch (error: any) {
            console.error('Failed to update credential:', error);
            const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to update credential.';
            throw new Error(errorMessage);
        } finally {
            isLoading.value = false;
        }
    };

    const deleteCredential = async (id: string): Promise<void> => {
        isLoading.value = true;
        try {
            await credentialApi.deleteCredential(id);

            // 로컬 상태에서 제거
            const index = credentials.value.findIndex(c => c.id === id);
            if (index !== -1) {
                credentials.value.splice(index, 1);
            }

            // 선택된 자격 증명이 삭제된 경우 클리어
            if (selectedCredential.value?.id === id) {
                selectedCredential.value = null;
            }
        } catch (error: any) {
            console.error('Failed to delete credential:', error);
            const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to delete credential.';
            throw new Error(errorMessage);
        } finally {
            isLoading.value = false;
        }
    };

    const selectCredential = (credential: Credential | null) => {
        selectedCredential.value = credential;
    };

    /**
     * 로컬 credentials 배열에서 ID로 자격 증명 찾기 (동기)
     * 이미 로드된 credentials에서 찾을 때 사용
     */
    const findCredentialById = (id: string): Credential | undefined => {
        return credentials.value.find(c => c.id === id);
    };

    /**
     * 서버에서 자격 증명 상세 조회 (비동기)
     * 서버에서 최신 데이터를 가져올 때 사용
     */
    const getCredentialById = async (id: string): Promise<Credential | null> => {
        try {
            const response = await credentialApi.getCredentialById(id);
            // apiClient는 response.data를 반환하므로, response 자체가 데이터
            const credential = (response as any)?.data || response;
            return credential || null;
        } catch (error: any) {
            console.error('Failed to get credential by id:', error);
            return null;
        }
    };

    return {
        // State
        credentials,
        selectedCredential,
        isLoading,
        // Getters
        hasCredentials,
        credentialCount,
        // Actions
        loadCredentials,
        addCredential,
        updateCredential,
        deleteCredential,
        selectCredential,
        findCredentialById,
        getCredentialById
    };
});
